import React, { useState, useEffect } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../context/ApiProvider';
import { OrganisationUnitInterface } from '../../../../shared';
import translateOrganisationUnitCategory from '../../../../utils/translateOrganisationUnitCategory';
import CustomAlert from '../../../CustomAlert/CustomAlert';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import { useAlertContext } from '../../../../context/AlertProvider';
import IconComponent from '../../../IconComponent/IconComponent';
import AddEditOrganisationUnit from './AddEditOrganisationUnit/AddEditOrganisationUnit';
import { StickyScrollableTableContainer } from '../../../DataTable/StickyScrollableTableContainer';

interface PopUpInterface {
  name?: string;
  id?: number;
  mode: 'add' | 'edit';
  isOpen: boolean;
}

interface DeletedInterface {
  id: number;
  name?: string;
  isOpen: boolean;
}

const defaultPopUp: PopUpInterface = {
  name: '',
  id: 0,
  mode: 'add',
  isOpen: false
};

const defaultDeleted: DeletedInterface = {
  id: 0,
  isOpen: false
};

const OrganisationUnits = () => {
  const { apiTenantOrganisationUnitController } = useApi();
  const { id: tenantId } = useParams();
  const { setShow: setShowAlert, setAlertProperties } = useAlertContext();

  const [organisationUnits, setOrganisationUnits] = useState<OrganisationUnitInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addEditPopUp, setAddEditPopUp] = useState(defaultPopUp);
  const [deletedElement, setDeletedElement] = useState(defaultDeleted);

  const columnHelper = createColumnHelper<OrganisationUnitInterface>();

  const fetchOrganizationUnits = async () => {
    try {
      setIsLoading(true);
      const result = await apiTenantOrganisationUnitController('').get(`/${tenantId}`);
      setOrganisationUnits(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) fetchOrganizationUnits();
  }, [tenantId]);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Klasa',
      cell: (info) => <p className="text-sm font-medium text-grayLight-900">{info.getValue()}</p>
    }),
    columnHelper.accessor('consumerCategory', {
      header: 'Kategoria',
      cell: (info) => (
        <p className="text-sm text-grayLight-900 w-80">
          {translateOrganisationUnitCategory(info.getValue())}
        </p>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Edycja',
      cell: ({ row }) => {
        const { id, name, consumerCategory } = row.original;
        if (consumerCategory !== 'CHILD') return null;
        return (
          <div className="flex gap-spacing-sm">
            <button
              onClick={() => setDeletedElement({ id, name, isOpen: true })}
              className="text-sm font-semibold text-grayLight-600 bg-transparent border-none">
              Usuń
            </button>
            <button
              onClick={() => setAddEditPopUp({ id, name, mode: 'edit', isOpen: true })}
              className="text-sm font-semibold text-grayWarm-950 bg-transparent border-none">
              Edytuj
            </button>
          </div>
        );
      },
      size: 120
    })
  ];

  return (
    <section className="SchoolClasses p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text fontSize="xl" fontWeight="semibold" color="gray.900">
            Klasy
          </Text>
          <Text className="w-2/3" fontSize="sm" color="gray.600">
            Lista klas w oddziale - tutaj możesz tworzyć nowe klasy, edytować istniejące lub je
            usuwać. Ważne! nie można usunąć klasy, w której są uczniowie - najpierw przenieś uczniów do
            innej klasy, np. utworzonej tymczasowej klasy, a następnie usuń klasę, którą chcesz
            usunąć z listy.
          </Text>
        </div>
        <CustomChakraButton
          size="md"
          hierarchy="primary"
          iconPosition="left"
          icon="plus"
          iconColor="#292524"
          buttonProps={{
            onClick: () => setAddEditPopUp({ ...defaultPopUp, isOpen: true })
          }}>
          Dodaj klasę
        </CustomChakraButton>
      </Box>

      <StickyScrollableTableContainer
        columns={columns}
        data={organisationUnits}
        isLoading={isLoading}
        enableSearch={true}
      />

      <AddEditOrganisationUnit
        isOpen={addEditPopUp.isOpen}
        unitId={addEditPopUp.id}
        unitName={addEditPopUp.name}
        mode={addEditPopUp.mode}
        onClose={() => {
          setAddEditPopUp(defaultPopUp);
          fetchOrganizationUnits();
        }}
      />

      <CustomAlert
        handleOpen={deletedElement.isOpen}
        header={`Usunąć klasę o nazwie: ${deletedElement.name}?`}
        content="Usunięcie klasy jest nieodwracalne."
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'warning' }}
        confirmButtonText="Usuń"
        onConfirmAction={async () => {
          try {
            await apiTenantOrganisationUnitController('').delete(
              `/${tenantId}/${deletedElement.id}`
            );
          } catch (error: any) {
            setAlertProperties({
              title: 'Błąd usuwania klasy',
              description: error?.response?.data?.errorMessage || 'Błąd usuwania',
              status: 'error',
              timeout: 9000
            });
            setShowAlert(true);
          } finally {
            setDeletedElement(defaultDeleted);
            fetchOrganizationUnits();
          }
        }}
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        cancelButtonText="Anuluj"
        onCancelAction={() => {
          setDeletedElement(defaultDeleted);
          fetchOrganizationUnits();
        }}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" color="#D92D20" />
          </div>
        }
      />
    </section>
  );
};

export default OrganisationUnits;
